// 인생 진행용 게임 규칙. 고서의 판단·초기 능력치 공식과 별도로 둔다.
import { GROUPS, GROUP_LABEL, ENGINE_RULES_VERSION } from './rules.js';
import { mulberry32 } from './rng.js';

export const LIFE_RULES = Object.freeze({ version: ENGINE_RULES_VERSION, timeCapacity: 3, fullFocusUnits: 2,
  actUnits: 2, alternativeUnits: 1, helpUnits: 2, limitedHelpUnits: 1 });
const TEN = [['비견', '겁재'], ['식신', '상관'], ['편재', '정재'], ['편관', '정관'], ['편인', '정인']];
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
export const eventRecords = (s) => Array.isArray(s.eventHistory) ? s.eventHistory :
  (s.log ?? []).filter((entry) => entry.kind === 'event' && entry.eventId).map((entry) => ({ ...entry,
    outcome: entry.outcome ?? (typeof entry.failed === 'boolean' ? entry.failed ? 'failure' : 'success' : null) }));

export function lifePhase(age) {
  if (age < 8) return { id: 'child', label: '유년기', question: '생활 환경에 익숙해지는 시기',
    record: '주변의 일과 사람을 접하며 유년기의 한 해를 보냈다.' };
  if (age < 15) return { id: 'school', label: '학령기', question: '배움과 활동을 접하는 시기',
    record: '배움과 활동을 접하며 학령기의 한 해를 보냈다.' };
  if (age < 24) return { id: 'youth', label: '탐색기', question: '무엇을 배우고 처음 시도할까?', record: '배움과 활동의 방향을 고른 해다.' };
  if (age < 36) return { id: 'entry', label: '사회 진입기', question: '어떤 일을 시작하고 이어갈까?', record: '일과 생활에서 맡을 범위를 정한 해다.' };
  if (age < 50) return { id: 'middle', label: '중년기', question: '역할을 넓힐까, 전문성을 깊게 할까?', record: '쌓아 온 경험의 다음 방향을 정한 해다.' };
  if (age < 60) return { id: 'transition', label: '전환기', question: '무엇을 계속하고 무엇을 넘길까?', record: '이어갈 일과 넘길 일을 구분한 해다.' };
  return { id: 'later', label: '후반기', question: '경험을 어떻게 활용하고 나눌까?', record: '경험을 활용할 활동과 관계를 고른 해다.' };
}

export function choiceTimeCost(choice) {
  if (!choice || choice.kind === 'decline') return 0;
  if (Number.isFinite(choice.timeCost)) return clamp(choice.timeCost, 0, LIFE_RULES.timeCapacity);
  return choice.kind === 'alternative' ? LIFE_RULES.alternativeUnits : LIFE_RULES.actUnits;
}

export function timePlan(s, { focus = null, focusUnits = focus ? 2 : 0, event = null, choiceIdx = null } = {}) {
  const capacity = s.age < 15 ? 0 : LIFE_RULES.timeCapacity;
  const allocatedFocus = s.age >= 15 && GROUPS.includes(focus) ? clamp(Math.round(focusUnits), 0, 2) : 0;
  const relationshipUnits = s.yearCommitment?.age === s.age ? s.yearCommitment.units ?? 0 : 0;
  const choice = choiceIdx == null ? null : event?.choices?.[choiceIdx];
  const eventUnits = choiceTimeCost(choice), used = allocatedFocus + relationshipUnits + eventUnits;
  return { capacity, focusField: allocatedFocus ? focus : null, focusUnits: allocatedFocus,
    eventUnits, relationshipUnits, used, remaining: capacity - used, valid: used <= capacity,
    eventId: event?.id ?? null, choiceKind: choice?.kind ?? (choice ? 'act' : null),
    reason: used > capacity ? `추가 활동 시간이 ${used - capacity}칸 부족합니다.` : '' };
}

export function natalProfile(s, field) {
  const basis = s.saju?.statBasis, index = GROUPS.indexOf(field);
  const force = basis?.tenGodForces, values = force && TEN.flat().map((key) => force[key]);
  if (!values?.every(Number.isFinite) || index < 0) return { kind: 'unknown', label: '원국 조건 미기록', share: null, usage: null };
  const total = values.reduce((sum, value) => sum + value, 0);
  const share = total > 0 ? TEN[index].reduce((sum, key) => sum + force[key], 0) / total : 0;
  const usage = basis.usageFactors?.[field], support = basis.daySupportIndex;
  const constrained = Number.isFinite(usage) && usage <= (field === '인성' ? .8 : .65);
  const kind = constrained ? 'capacity' : share >= .28 ? 'high' : share <= .10 ? 'entry' : 'balanced';
  const label = { capacity: '맡을 범위 조절', high: '축적을 활용', entry: '작게 시작', balanced: '방법을 선택' }[kind];
  return { kind, label, share, usage, support, uncertain: !!basis.hourUncertainty };
}

const OPPORTUNITIES = {
  비겁: { ready: '함께할 활동의 역할을 나눌 기회', prepare: '짧은 만남으로 활동 방식을 알아볼 기회', retry: '지난 협의의 범위를 줄여 다시 만날 기회' },
  식상: { ready: '만들어 본 결과물을 공개할 기회', prepare: '작은 작업 한 가지를 배워 볼 기회', retry: '중단한 작업을 작게 고쳐 다시 시도할 기회' },
  재성: { ready: '검토 경험을 활용해 작은 제안을 비교할 기회', prepare: '비용과 조건을 조사할 기회', retry: '이전 계획의 가정을 바꿔 다시 검토할 기회' },
  관성: { ready: '맡아 본 역할을 넓혀 볼 기회', prepare: '기한과 책임 범위를 정해 볼 기회', retry: '지난 역할의 부담을 줄여 다시 맡을 기회' },
  인성: { ready: '배운 내용을 적용하거나 설명할 기회', prepare: '입문 자료와 학습 방식을 알아볼 기회', retry: '막혔던 단원을 줄여 다시 배울 기회' },
};

export function preparationFor(s, field) {
  const records = eventRecords(s).filter((entry) => (entry.field ?? entry.targetField) === field
    || (entry.effects?.[field] ?? 0) > 0);
  const attempts = records.filter((entry) => entry.outcome && entry.outcome !== 'skipped');
  const successes = attempts.filter((entry) => entry.outcome === 'success'
    && ((entry.field ?? entry.targetField) === field || (entry.effects?.[field] ?? 0) > 0));
  const failures = attempts.filter((entry) => entry.outcome === 'failure');
  const last = attempts.at(-1);
  const focusAges = new Set((s.log ?? []).filter((entry) => entry.kind === 'focus' && entry.field === field).map((entry) => entry.age));
  for (const year of s.history ?? []) if (year.timePlan?.focusField === field) focusAges.add(year.age);
  const focusYears = focusAges.size;
  const stage = last?.outcome === 'failure' ? 'retry' : successes.length || focusYears >= 2 ? 'ready' : 'prepare';
  return { field, stage, attempts: attempts.length, successes: successes.length, failures: failures.length,
    focusYears };
}

export function concreteOpportunities(s, op) {
  if (s.age < 15) return [];
  return (op?.open ?? []).map((field) => {
    const preparation = preparationFor(s, field), profile = natalProfile(s, field);
    return { ...preparation, label: GROUP_LABEL[field], title: OPPORTUNITIES[field][preparation.stage],
      description: `${preparation.focusYears}년 집중 · ${preparation.attempts}회 시도 · ${preparation.successes}회 성공 · ${preparation.failures}회 실패. ${profile.label} 방식으로 접근할 수 있습니다.` };
  });
}

const sum = (text) => [...String(text)].reduce((value, char) => Math.imul(value ^ char.charCodeAt(0), 16777619) >>> 0, 2166136261);
export function personAvailability(s, person) {
  if (person.status !== 'near') return { available: false, label: '소원해짐', reason: '현재 함께할 관계가 아닙니다.' };
  if (s.yearCommitment?.age === s.age && s.yearCommitment.actions?.some((action) => action.personId === person.id)) {
    return { available: true, label: '함께하기로 한 일정', reason: '올해 함께할 활동 시간을 이미 정했습니다.' };
  }
  if (person.promise?.status === 'pending' && person.promise.dueAge <= s.age) {
    return { available: true, label: '약속한 일정', reason: '이전에 약속한 일을 확인할 때입니다.' };
  }
  const available = mulberry32((s.seed + s.age * 7919 + sum(person.id) + 83 * 104729) >>> 0)() >= .25;
  return { available, label: available ? '일정 가능' : '올해 일정 겹침',
    reason: available ? '올해 활동을 함께할 수 있습니다.' : '올해 함께할 일정이 맞지 않습니다.' };
}

export function sceneMilestones(s) {
  const records = eventRecords(s);
  const learningYears = new Set((s.history ?? []).filter((year) => year.acted?.includes('인성')).map((year) => year.age));
  const work = records.filter((entry) => (entry.field ?? entry.targetField) === '식상' || (entry.effects?.식상 ?? 0) > 0);
  const completed = work.filter((entry) => entry.outcome === 'success'
    && ((entry.field ?? entry.targetField) === '식상' || (entry.effects?.식상 ?? 0) > 0));
  const latest = work.at(-1);
  return { learningCount: learningYears.size, completedCount: completed.length,
    workStatus: !latest ? 'none' : latest.outcome === 'success' ? 'complete' : latest.outcome === 'failure' ? 'active' : 'paused',
    workTitle: latest?.title ?? null, peopleCount: (s.people ?? []).filter((person) => person.status === 'near').length,
    careerTitle: s.job ? s.job.level <= 3 ? s.job.levels?.[s.job.level - 1] : s.job.branch?.name : null };
}

export function referenceFingerprint(data) { return sum(JSON.stringify({ events: data.events, jobs: data.jobs })).toString(16).padStart(8, '0'); }
